import { Authenticated, Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import {
  ErrorComponent,
  RefineSnackbarProvider,
  ThemedLayout,
  useNotificationProvider,
} from "@refinedev/mui";
import Navkit from '@taruvi/navkit';
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import CircularProgress from "@mui/material/CircularProgress";
import routerProvider, {
  DocumentTitleHandler,
} from "@refinedev/react-router";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";
import { taruviClient } from "./taruviClient";
import {
  taruviDataProvider,
  taruviAuthProvider,
  taruviStorageProvider,
  taruviAppProvider,
  taruviUserProvider,
  taruviAccessControlProvider,
} from "./providers/refineProviders";
import { CustomSider } from "./components/sidenav";
import { LoginRedirect } from "./components/auth/LoginRedirect";
import { RouteGuard } from "./components/RouteGuard";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { RefineThemes } from "@refinedev/mui";

import React, { useRef, useEffect, useState, lazy, Suspense } from "react";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import { AccessGate } from "./components/auth/AccessGate";
import { ScrollToTop } from "./components/ScrollToTop";
import { UnsavedChangesDialog } from "./components/UnsavedChangesDialog";
import { queryClient } from "./config/queryClient";



// Lazy load page components for code splitting
const Dashboard = lazy(() => import("./pages/dashboard").then(m => ({ default: m.Dashboard })));
const EmployeeList = lazy(() => import("./pages/employees").then(m => ({ default: m.EmployeeList })));
const EmployeeShow = lazy(() => import("./pages/employees").then(m => ({ default: m.EmployeeShow })));
const EmployeeCreate = lazy(() => import("./pages/employees").then(m => ({ default: m.EmployeeCreate })));
const EmployeeEdit = lazy(() => import("./pages/employees").then(m => ({ default: m.EmployeeEdit })));
const DepartmentList = lazy(() => import("./pages/departments").then(m => ({ default: m.DepartmentList })));
const DepartmentShow = lazy(() => import("./pages/departments").then(m => ({ default: m.DepartmentShow })));
const DepartmentCreate = lazy(() => import("./pages/departments").then(m => ({ default: m.DepartmentCreate })));
const DepartmentEdit = lazy(() => import("./pages/departments").then(m => ({ default: m.DepartmentEdit })));
const TeamList = lazy(() => import("./pages/teams").then(m => ({ default: m.TeamList })));
const TeamShow = lazy(() => import("./pages/teams").then(m => ({ default: m.TeamShow })));
const TeamCreate = lazy(() => import("./pages/teams").then(m => ({ default: m.TeamCreate })));
const TeamEdit = lazy(() => import("./pages/teams").then(m => ({ default: m.TeamEdit })));
const OrgChartPage = lazy(() => import("./pages/org-chart").then(m => ({ default: m.OrgChartPage })));
const SettingsPage = lazy(() => import("./pages/settings").then(m => ({ default: m.SettingsPage })));
const MyProfilePage = lazy(() => import("./pages/me").then(m => ({ default: m.MyProfilePage })));
const MyTeamsPage = lazy(() => import("./pages/my-teams").then(m => ({ default: m.MyTeamsPage })));


// Loading fallback component
const PageLoader = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
    <CircularProgress sx={{ color: "#4A7C7E" }} />
  </Box>
);

// Conditionally import DevTools only in development
const DevtoolsProvider = import.meta.env.DEV
  ? lazy(() => import("@refinedev/devtools").then(m => ({ default: m.DevtoolsProvider })))
  : ({ children }: { children: React.ReactNode }) => <>{children}</>;

const DevtoolsPanel = import.meta.env.DEV
  ? lazy(() => import("@refinedev/devtools").then(m => ({ default: m.DevtoolsPanel })))
  : () => null;

const AppContent = () => {
  const [mode, setMode] = useState<"light" | "dark">("light");
  const theme = React.useMemo(() => createTheme(mode === "dark" ? RefineThemes.BlueDark as any : RefineThemes.Blue as any), [mode]);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (navRef.current) {
      const height = navRef.current.offsetHeight;
      document.documentElement.style.setProperty('--nav-height', `${height}px`);
    }
  }, []);

  return (
    <>
      <div
        ref={navRef}
        data-nav-container
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1300,
          width: '100%',
        }}
      >
        <Navkit
          client={taruviClient}
          getTheme={(t) => setMode(t as "light" | "dark")}
        />
      </div>
      <ThemeProvider theme={theme}>
        <RefineSnackbarProvider>
          <Suspense fallback={<PageLoader />}>
            <DevtoolsProvider>
              <Refine
                dataProvider={{
                  default: taruviDataProvider,
                  storage: taruviStorageProvider,
                  app: taruviAppProvider,
                  user: taruviUserProvider,
                }}
                notificationProvider={useNotificationProvider}
                routerProvider={routerProvider}
                authProvider={taruviAuthProvider}
                accessControlProvider={taruviAccessControlProvider}
                resources={[
                  {
                    name: "me",
                    list: "/me",
                    meta: {
                      label: "My Profile",
                      icon: <PersonIcon />,
                      aclResource: "custom:me",
                      action: "read"
                    },
                  },
                  {
                    name: "my-teams",
                    list: "/my-teams",
                    meta: {
                      label: "My Teams",
                      icon: <GroupsIcon />,
                      aclResource: "custom:my-teams",
                      action: "read"
                    },
                  },
                  {
                    name: "employees",
                    list: "/employees",
                    create: "/employees/create",
                    edit: "/employees/edit/:id",
                    show: "/employees/show/:id",
                    meta: {
                      canDelete: true,
                      label: "Employees",
                      icon: <PeopleIcon />,
                      aclResource: "datatable:employees",
                      action: "update"
                    },
                  },
                  {
                    name: "departments",
                    list: "/departments",
                    create: "/departments/create",
                    edit: "/departments/edit/:id",
                    show: "/departments/show/:id",
                    meta: {
                      canDelete: true,
                      label: "Departments",
                      icon: <BusinessIcon />,
                      aclResource: "datatable:departments",
                      action: "create"
                    },
                  },
                  {
                    name: "teams",
                    list: "/teams",
                    create: "/teams/create",
                    edit: "/teams/edit/:id",
                    show: "/teams/show/:id",
                    meta: {
                      canDelete: true,
                      label: "Teams",
                      icon: <GroupsIcon />,
                      aclResource: "datatable:teams",
                      action: "update"
                    },
                  },
                  {
                    name: "org-chart",
                    list: "/org-chart",
                    meta: {
                      label: "Org Chart",
                      icon: <AccountTreeIcon />,
                      aclResource: "custom:org-chart",
                      action: "read"
                    },
                  },
                  {
                    name: "settings",
                    list: "/settings",
                    meta: {
                      label: "Settings",
                      icon: <SettingsIcon />,
                      aclResource: "custom:settings",
                      action: "create"
                    },
                  },
                ]}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  projectId: "obEpHJ-M7JimA-31GF1J",
                  reactQuery: {
                    clientConfig: queryClient,
                  },
                }}
              >
                <Routes>
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-inner"
                        fallback={<LoginRedirect />}
                      >
                        <AccessGate>
                          <ThemedLayout Header={() => null} Sider={CustomSider} initialSiderCollapsed={true}>
                            <Box sx={{ ml: { xs: 0, md: '64px' }, transition: 'margin-left 0.2s ease-in-out' }}>
                              <Suspense fallback={<PageLoader />}>
                                <Outlet />
                              </Suspense>
                            </Box>
                          </ThemedLayout>
                        </AccessGate>
                      </Authenticated>
                    }
                  >
                    <Route index element={<Dashboard />} />
                    <Route path="/me" element={<MyProfilePage />} />
                    <Route path="/my-teams" element={<MyTeamsPage />} />
                    <Route path="/employees" element={<RouteGuard resource="datatable:employees" action="update"><EmployeeList /></RouteGuard>} />
                    <Route path="/employees/show/:id" element={<EmployeeShow />} />
                    <Route path="/employees/create" element={<RouteGuard resource="datatable:employees" action="create"><EmployeeCreate /></RouteGuard>} />
                    <Route path="/employees/edit/:id" element={<RouteGuard resource="datatable:employees" action="update"><EmployeeEdit /></RouteGuard>} />
                    <Route path="/departments" element={<RouteGuard resource="datatable:departments" action="create"><DepartmentList /></RouteGuard>} />
                    <Route path="/departments/show/:id" element={<RouteGuard resource="datatable:departments" action="create"><DepartmentShow /></RouteGuard>} />
                    <Route path="/departments/create" element={<RouteGuard resource="datatable:departments" action="create"><DepartmentCreate /></RouteGuard>} />
                    <Route path="/departments/edit/:id" element={<RouteGuard resource="datatable:departments" action="update"><DepartmentEdit /></RouteGuard>} />
                    <Route path="/teams" element={<RouteGuard resource="datatable:teams" action="update"><TeamList /></RouteGuard>} />
                    <Route path="/teams/show/:id" element={<RouteGuard resource="datatable:teams" action="update"><TeamShow /></RouteGuard>} />
                    <Route path="/teams/create" element={<RouteGuard resource="datatable:teams" action="create"><TeamCreate /></RouteGuard>} />
                    <Route path="/teams/edit/:id" element={<RouteGuard resource="datatable:teams" action="update"><TeamEdit /></RouteGuard>} />
                    <Route path="/org-chart" element={<OrgChartPage />} />
                    <Route path="/settings" element={<RouteGuard resource="custom:settings" action="create"><SettingsPage /></RouteGuard>} />
                    <Route path="*" element={<ErrorComponent />} />
                  </Route>
                </Routes>

                <RefineKbar />
                <UnsavedChangesDialog />
                <DocumentTitleHandler handler={() => "People"} />
              </Refine>
              <DevtoolsPanel />
            </DevtoolsProvider>
          </Suspense>
        </RefineSnackbarProvider>
      </ThemeProvider>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <RefineKbarProvider>
        <CssBaseline />
        <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
        <AppContent />
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
